import { Box, Checkbox, Divider, FormControlLabel, MenuItem, Paper, TextField, Typography } from '@mui/material';
import { NumericTextField } from '../../../../components/forms/NumericTextField';
import { FieldEvidenceButton } from '../../../../components/evidence/FieldEvidenceButton';
import type { RockBTSSpecimen } from '../../../../types/rockBts';
import type { BtsSpecimenPreview } from '../computations';
import { BTS_CONDITION_OPTIONS, BTS_POW_OPTIONS } from '../rockBtsFields';
// Shared section idiom (reused read-only from the Rock TCS module)
import { DerivedValue, fmt, inputSx, paperSx, SectionHeader, unit } from '../../rock-tcs/sectionStyles';
import { useTestFormulas } from '../../../../hooks/useTestFormulas';
import { ReferenceFigureButton } from './ReferenceFigureButton';
import axisAnglesFigure from '../../../../assets/rock-bts-axis-angles.png';

interface Props {
  specimen: RockBTSSpecimen;
  preview: BtsSpecimenPreview;
  title: string;
  onChange: (field: keyof RockBTSSpecimen, value: RockBTSSpecimen[keyof RockBTSSpecimen]) => void;
  readOnly?: boolean;
}

type PowField = (typeof BTS_POW_OPTIONS)[number]['field'];

/**
 * Test Parameters — one specimen's card (mockup: Rock BTS Test Parameter.png):
 * Specimen Condition (REQ-01/D7), Plane of Weakness Assessment (REQ-02),
 * Specimen Dimensions + Physical Properties (REQ-04), ASTM Load Rate Guidance (REQ-06).
 */
export const SpecimenParameterCard = ({ specimen, preview, title, onChange, readOnly }: Props) => {
  const num = (field: keyof RockBTSSpecimen) => (value: number | null) => onChange(field, value);
  // Formula-registry tooltips for the computed tiles (FEAT "Formula Registry").
  const { formulaFor } = useTestFormulas('rockBts');

  const onPowToggle = (field: PowField, checked: boolean) => {
    if (readOnly) return;
    onChange(field, checked);
    // 'None' is mutually exclusive with the other options (UI rule, BTS-UI-FIELD-MAP)
    if (checked && field === 'powNone') {
      for (const opt of BTS_POW_OPTIONS) {
        if (opt.field !== 'powNone' && specimen[opt.field]) onChange(opt.field, false);
      }
    } else if (checked && specimen.powNone) {
      onChange('powNone', false);
    }
  };

  return (
    <Paper variant="outlined" sx={paperSx}>
      <SectionHeader label={title} />

      {/* ── Specimen Condition ── */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25, mb: 1.5 }}>
        <TextField
          select size="small" label="Condition *"
          value={specimen.condition ?? ''}
          onChange={(e) => onChange('condition', e.target.value || null)}
          InputProps={{ readOnly }} sx={inputSx}
        >
          {BTS_CONDITION_OPTIONS.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
        </TextField>
        {specimen.condition === 'Other' ? (
          <TextField
            size="small" label="If other, specify"
            value={specimen.conditionOther ?? ''}
            onChange={(e) => onChange('conditionOther', e.target.value || null)}
            InputProps={{ readOnly }} sx={inputSx}
          />
        ) : (
          <TextField
            size="small" label="Remarks"
            value={specimen.conditionRemarks ?? ''}
            onChange={(e) => onChange('conditionRemarks', e.target.value || null)}
            InputProps={{ readOnly }} sx={inputSx}
          />
        )}
      </Box>

      {/* ── Plane of Weakness Assessment (REQ-02) ── */}
      <Divider sx={{ mb: 1 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <SectionHeader label="Plane of Weakness Assessment" color="text.secondary" />
        <ReferenceFigureButton
          title="Orientation angles — load axis (a) / core axis (b)"
          image={axisAnglesFigure}
        />
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', display: 'block', mb: 0.25 }}>
        Plane of weakness present (select all that apply)
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 1, mb: 0.75 }}>
        {BTS_POW_OPTIONS.map((opt) => (
          <FormControlLabel
            key={opt.field}
            control={
              <Checkbox
                size="small"
                checked={!!specimen[opt.field]}
                onChange={(e) => onPowToggle(opt.field, e.target.checked)}
                sx={{ py: 0.25 }}
              />
            }
            label={<Typography variant="body2" sx={{ fontSize: '0.76rem' }}>{opt.label}</Typography>}
          />
        ))}
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1.25, mb: 1.5 }}>
        <TextField
          size="small" label="Other (specify)"
          value={specimen.powOtherText ?? ''}
          onChange={(e) => onChange('powOtherText', e.target.value || null)}
          InputProps={{ readOnly }} sx={inputSx}
          disabled={!specimen.powOther}
        />
        <NumericTextField
          size="small" label="Orientation vs load axis (a)"
          value={specimen.powAngleADeg} onChange={num('powAngleADeg')}
          InputProps={{ endAdornment: unit('°'), readOnly }} sx={inputSx}
        />
        <NumericTextField
          size="small" label="Orientation vs core axis (b)"
          value={specimen.powAngleBDeg} onChange={num('powAngleBDeg')}
          InputProps={{ endAdornment: unit('°'), readOnly }} sx={inputSx}
        />
      </Box>

      {/* ── Specimen Dimensions (REQ-04) ── */}
      <Divider sx={{ mb: 1 }} />
      <SectionHeader label="Specimen Dimensions" color="text.secondary" />
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 1 }}>
        <Box sx={{ display: 'grid', rowGap: 1.25 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
            DIAMETER MEASUREMENTS (mm)
          </Typography>
          {[1, 2, 3].map((i) => (
            <NumericTextField
              key={`d${i}`} size="small" label={`Diameter ${i}`}
              value={specimen[`diameter${i}` as keyof RockBTSSpecimen] as number | null}
              onChange={num(`diameter${i}` as keyof RockBTSSpecimen)}
              InputProps={{ endAdornment: unit('mm'), readOnly }} sx={inputSx}
            />
          ))}
          <DerivedValue
            label="Average Diameter"
            value={fmt(preview.avgDiameterMm, 2)}
            suffix="mm"
            formula={formulaFor('avgDiameterMm')?.formula}
            note={formulaFor('avgDiameterMm')?.note}
          />
        </Box>
        <Box sx={{ display: 'grid', rowGap: 1.25 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
            THICKNESS MEASUREMENTS (mm)
          </Typography>
          {[1, 2, 3].map((i) => (
            <NumericTextField
              key={`t${i}`} size="small" label={`Thickness ${i}`}
              value={specimen[`thickness${i}` as keyof RockBTSSpecimen] as number | null}
              onChange={num(`thickness${i}` as keyof RockBTSSpecimen)}
              InputProps={{ endAdornment: unit('mm'), readOnly }} sx={inputSx}
            />
          ))}
          <DerivedValue
            label="Average Thickness"
            value={fmt(preview.avgThicknessMm, 2)}
            suffix="mm"
            formula={formulaFor('avgThicknessMm')?.formula}
            note={formulaFor('avgThicknessMm')?.note}
          />
        </Box>
      </Box>

      {/* ── Physical Properties ── */}
      <Divider sx={{ mb: 1 }} />
      <SectionHeader label="Physical Properties" color="text.secondary" />
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1.25, alignItems: 'center', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <NumericTextField
            size="small" label="Mass *"
            value={specimen.massG} onChange={num('massG')}
            InputProps={{ endAdornment: unit('g'), readOnly }} sx={{ ...inputSx, flex: 1 }}
          />
          <FieldEvidenceButton fieldKey={`mass${specimen.specimenNumber}`} />
        </Box>
        <DerivedValue
          label="Volume (calculated)"
          value={fmt(preview.volumeCm3, 2)}
          suffix="cm³"
          formula={formulaFor('volumeCm3')?.formula}
          note={formulaFor('volumeCm3')?.note}
        />
        <DerivedValue
          label="Bulk Density (calculated)"
          value={fmt(preview.bulkDensityGCm3, 2)}
          suffix="g/cm³"
          formula={formulaFor('bulkDensityGCm3')?.formula}
          note={formulaFor('bulkDensityGCm3')?.note}
        />
      </Box>

      {/* ── ASTM Load Rate Guidance (REQ-06; live from this specimen's averages) ── */}
      <Divider sx={{ mb: 1 }} />
      <SectionHeader label="ASTM Load Rate Guidance (from ASTM D3967)" color="text.secondary" />
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25 }}>
        <DerivedValue
          label="Allowable range (MPa/s)"
          value={`${preview.minRateMPas.toFixed(2)} – ${preview.maxRateMPas.toFixed(2)}`}
        />
        <DerivedValue
          label="Allowable range (kN/s)"
          value={
            preview.minRateKNs !== null && preview.maxRateKNs !== null
              ? `${fmt(preview.minRateKNs, 2)} – ${fmt(preview.maxRateKNs, 2)}`
              : '—'
          }
          formula={formulaFor('minRateKNs')?.formula}
          note="Range = [Min, Max] kN/s; Max = 0.35·(π·Avg D·Avg t)/2/1000 (ASTM D3967 band)."
        />
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.66rem', display: 'block', mt: 0.5 }}>
        Range converts to kN/s from this specimen's average diameter and thickness. The actual rate is entered
        on Result Entry in kN/s.
      </Typography>
    </Paper>
  );
};
